import { useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CARD_LABEL_CLASS, INPUT_CLASS } from './ProductFormField';
import { OrderMethodPicker } from './OrderMethodPicker';
import type { VersionFormRow } from './productFormState';

// Selectors are an open key/value map, but keys must come from this registry
// (mirrors services/products/versionResolution.ts).
const AXIS_VALUES: Record<string, string[]> = {
    market: ['US', 'CA'],
    program: ['Vivos', 'Concierge', 'Adjunct']
};

// Editable selector pill (key : value) with a remove affordance.
function SelectorEditPill({ k, v, onRemove }: { k: string; v: string; onRemove: () => void }) {
    return (
        <span className="inline-flex items-center gap-1 bg-white border border-gray-300 rounded-lg h-8 pl-2 pr-1 text-[12px]">
            <span className="text-gray-400">{k}</span>
            <span className="text-gray-300">:</span>
            <span className="font-medium text-gray-700">{v}</span>
            <button
                type="button"
                onClick={onRemove}
                className="w-5 h-5 rounded flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50"
                aria-label={`Remove ${k} selector`}
            >
                <XMarkIcon className="h-3.5 w-3.5" />
            </button>
        </span>
    );
}

// "Add selector" flow: dashed button, then axis key picker, then value picker.
function AddSelector({
    usedKeys,
    onAdd
}: {
    usedKeys: string[];
    onAdd: (key: string, value: string) => void;
}) {
    const [pickingKey, setPickingKey] = useState(false);
    const [key, setKey] = useState('');
    const availableKeys = Object.keys(AXIS_VALUES).filter((axis) => !usedKeys.includes(axis));

    if (availableKeys.length === 0) return null;

    if (!pickingKey) {
        return (
            <button
                type="button"
                onClick={() => setPickingKey(true)}
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-dashed border-gray-300 text-[12px] font-semibold text-gray-500 hover:bg-gray-50"
            >
                <PlusIcon className="h-3.5 w-3.5" strokeWidth={2} />
                Add selector
            </button>
        );
    }

    const reset = () => {
        setPickingKey(false);
        setKey('');
    };

    return (
        <span className="inline-flex items-center gap-1.5">
            <select
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className="h-8 border border-gray-300 rounded-lg text-[12px] text-gray-700 px-1.5 focus:outline-none focus:border-gray-800"
                autoFocus
            >
                <option value="" disabled>
                    key…
                </option>
                {availableKeys.map((axis) => (
                    <option key={axis} value={axis}>
                        {axis}
                    </option>
                ))}
            </select>
            {key && (
                <select
                    value=""
                    onChange={(e) => {
                        onAdd(key, e.target.value);
                        reset();
                    }}
                    className="h-8 border border-gray-300 rounded-lg text-[12px] text-gray-700 px-1.5 focus:outline-none focus:border-gray-800"
                >
                    <option value="" disabled>
                        value…
                    </option>
                    {AXIS_VALUES[key].map((value) => (
                        <option key={value} value={value}>
                            {value}
                        </option>
                    ))}
                </select>
            )}
            <button
                type="button"
                onClick={reset}
                className="w-6 h-6 rounded flex items-center justify-center text-gray-300 hover:text-gray-500"
                aria-label="Cancel add selector"
            >
                <XMarkIcon className="h-3.5 w-3.5" />
            </button>
        </span>
    );
}

interface VersionEditCardProps {
    row: VersionFormRow;
    canRemove: boolean;
    /** Pack size only applies to webstore products; the part number does not. */
    showWebstoreFields: boolean;
    // The product's vendor - every version's method must belong to it.
    vendorId: string;
    disabled?: boolean;
    onChange: (patch: Partial<VersionFormRow>) => void;
    onRemove: () => void;
}

export function VersionEditCard({
    row,
    canRemove,
    showWebstoreFields,
    vendorId,
    disabled,
    onChange,
    onRemove
}: VersionEditCardProps) {
    const removeSelector = (key: string) => {
        const next = { ...row.selectors };
        delete next[key];
        onChange({ selectors: next });
    };

    // Rides the top row for a plain product, drops next to the part number
    // once pack size takes its place.
    const vendorCostField = (
        <label className="flex flex-col gap-1">
            <span className={CARD_LABEL_CLASS}>Vendor cost</span>
            <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400">
                    $
                </span>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={row.vendorCost}
                    onChange={(e) => onChange({ vendorCost: e.target.value })}
                    className={`${INPUT_CLASS} pl-6 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none`}
                />
            </div>
        </label>
    );

    return (
        <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-3">
            <div
                className={`grid ${
                    showWebstoreFields
                        ? 'grid-cols-[1.2fr_0.9fr_0.6fr_1.1fr_auto]'
                        : 'grid-cols-[1.2fr_0.9fr_0.9fr_1.1fr_auto]'
                } gap-2.5 items-end`}
            >
                <label className="flex flex-col gap-1">
                    <span className={CARD_LABEL_CLASS}>Label *</span>
                    <input
                        type="text"
                        value={row.label}
                        onChange={(e) => onChange({ label: e.target.value })}
                        placeholder="e.g. US, Vivos, Standard"
                        required
                        className={INPUT_CLASS}
                    />
                </label>
                <label className="flex flex-col gap-1">
                    <span className={CARD_LABEL_CLASS}>Price *</span>
                    <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400">
                            $
                        </span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.price}
                            onChange={(e) => onChange({ price: e.target.value })}
                            required
                            className={`${INPUT_CLASS} pl-6 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none`}
                        />
                    </div>
                </label>
                {showWebstoreFields ? (
                    <label
                        className="flex flex-col gap-1"
                        title="Units contained in one sellable box (1 box = 3 adapters). A multiplier for the vendor PO, not a quantity and not a price."
                    >
                        <span className={CARD_LABEL_CLASS}>Pack size</span>
                        <input
                            type="number"
                            min="1"
                            step="1"
                            value={row.packSize}
                            onChange={(e) => onChange({ packSize: e.target.value })}
                            className={`${INPUT_CLASS} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none`}
                        />
                    </label>
                ) : (
                    vendorCostField
                )}
                <label className="flex flex-col gap-1">
                    <span className={CARD_LABEL_CLASS}>Bill.com ID</span>
                    <input
                        type="text"
                        value={row.billId}
                        onChange={(e) => onChange({ billId: e.target.value })}
                        className={`${INPUT_CLASS} font-mono !text-[12px]`}
                    />
                </label>
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={!canRemove}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-300 shrink-0"
                    aria-label="Remove version"
                >
                    <XMarkIcon className="h-4 w-4" />
                </button>
            </div>
            <div
                className={`grid gap-2.5 items-end ${
                    showWebstoreFields
                        ? 'grid-cols-[0.9fr_1.4fr_1.4fr]'
                        : 'grid-cols-[1.4fr_1.4fr_0.9fr]'
                }`}
            >
                {showWebstoreFields && vendorCostField}
                <label className="flex flex-col gap-1">
                    <span className={CARD_LABEL_CLASS}>Vendor part number</span>
                    <input
                        type="text"
                        value={row.vendorPartNumber}
                        onChange={(e) => onChange({ vendorPartNumber: e.target.value })}
                        className={`${INPUT_CLASS} font-mono !text-[12px]`}
                    />
                </label>
                <label className="flex flex-col gap-1">
                    <span className={CARD_LABEL_CLASS}>Expires on</span>
                    <div className="flex items-center gap-1">
                        <input
                            type="date"
                            value={row.expiresAt}
                            onChange={(e) => onChange({ expiresAt: e.target.value })}
                            className={`${INPUT_CLASS} min-w-0`}
                        />
                        {row.expiresAt && (
                            <button
                                type="button"
                                onClick={() => onChange({ expiresAt: '' })}
                                className="w-6 h-6 rounded flex items-center justify-center text-gray-300 hover:text-gray-500 shrink-0"
                                aria-label="Clear expiry date"
                            >
                                <XMarkIcon className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </label>
            </div>
            <div className="max-w-xs">
                <OrderMethodPicker
                    id={`version-order-method-${row._key}`}
                    vendorId={vendorId}
                    value={row.orderMethodId}
                    disabled={disabled}
                    onChange={(orderMethodId) => onChange({ orderMethodId })}
                />
            </div>
            <label className="inline-flex items-center gap-2 self-start">
                <input
                    type="checkbox"
                    checked={row.isDefault}
                    disabled={disabled}
                    onChange={(e) => onChange({ isDefault: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-300 disabled:opacity-40"
                />
                <span className="text-[13px] font-semibold text-gray-700">Default</span>
                <span className="text-[11px] text-gray-400">
                    Billed when the Dx report names no version
                </span>
            </label>
            <div className="flex flex-col gap-1.5">
                <span className={CARD_LABEL_CLASS}>Selectors</span>
                <div className="flex flex-wrap items-center gap-1.5">
                    {Object.entries(row.selectors).map(([key, value]) => (
                        <SelectorEditPill
                            key={key}
                            k={key}
                            v={value}
                            onRemove={() => removeSelector(key)}
                        />
                    ))}
                    <AddSelector
                        usedKeys={Object.keys(row.selectors)}
                        onAdd={(key, value) =>
                            onChange({ selectors: { ...row.selectors, [key]: value } })
                        }
                    />
                </div>
            </div>
        </div>
    );
}
